// src/pages/OrdersPage.js
import React, { useContext, useEffect, useState } from "react";
import { LanguageContext } from "../context/LanguageContext";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../services/api";

function OrdersPage() {
  const { lang } = useContext(LanguageContext);
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadOrders = async () => {
      try {
        const data = await apiRequest("/orders/my");
        setOrders(data);
      } catch (err) {
        console.error(err);
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };

    loadOrders();
  }, []);

  const TEXT = {
    EN: {
      title: "My Orders",
      back: "×",
      noOrders: "You have no previous orders.",
      status: "Status",
      total: "Total",
      items: "Items",
    },
    AR: {
      title: "طلباتي",
      back: "×",
      noOrders: "لا يوجد طلبات سابقة.",
      status: "الحالة",
      total: "الإجمالي",
      items: "المنتجات",
    },
  };

  const t = TEXT[lang];

  return (
    <div
      className="
        w-full min-h-screen
        bg-[#0F1A12] text-white
        pt-[95px] px-5 pb-24
      "
      dir={lang === "AR" ? "rtl" : "ltr"}
    >
      {/* BACK */}
      <button
        onClick={() => navigate(-1)}
        className="absolute top-[80px] left-4 text-3xl opacity-60 hover:opacity-100"
      >
        {t.back}
      </button>

      {/* TITLE */}
      <h1 className="text-xl font-semibold text-center">
        {t.title}
      </h1>
      <div className="h-[2px] w-16 bg-white mx-auto mt-2 mb-6"></div>

      {/* LOADING */}
      {loading && (
        <p className="text-center text-white/60 mt-20">
          Loading orders…
        </p>
      )}

      {/* EMPTY */}
      {!loading && orders.length === 0 && (
        <p className="text-center text-white/60 mt-20">
          {t.noOrders}
        </p>
      )}

      {/* LIST */}
      {!loading && orders.length > 0 && (
        <div className="flex flex-col gap-6 max-w-xl mx-auto">
          {orders.map((order) => (
            <div
              key={order.id}
              className="
                border border-white/10 rounded-xl
                p-5 bg-[#121F16]
                shadow-md
              "
            >
              {/* HEADER */}
              <div className="flex justify-between items-center mb-3">
                <span className="font-semibold">
                  {order.order_number}
                </span>
                <span className="text-xs text-white/60">
                  {new Date(order.created_at).toLocaleDateString()}
                </span>
              </div>

              {/* STATUS */}
              <div className="checkout-line mb-2">
                <span>{t.status}</span>
                <span
                  className={`capitalize font-medium ${
                    order.status === "delivered"
                      ? "text-green-500"
                      : order.status === "pending"
                      ? "text-orange-400"
                      : "text-white/60"
                  }`}
                >
                  {order.status}
                </span>
              </div>

              {order.coupon_code && (
                <div className="text-xs text-green-500 mt-1">
                  Coupon: {order.coupon_code}
                </div>
              )}

              {/* TOTAL */}
              <div className="checkout-line mb-4">
                <span>
                  {t.items} ({order.total_items})
                </span>
                <span className="font-semibold">
                  ${order.total_amount}
                </span>
              </div>

              {/* ITEMS */}
              <div className="flex flex-col gap-3">
                {order.items?.map((item, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-3"
                  >
                    {/* IMAGE */}
                    <div className="w-9 h-9 rounded-full bg-white/10 overflow-hidden flex-shrink-0">
                      <img
                        src={item.image || "/images/placeholder-product.jpg"}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* INFO */}
                    <div className="flex-1">
                      <p className="text-sm font-medium leading-tight">
                        {lang === "EN"
                          ? item.product_name_en
                          : item.product_name_ar}
                      </p>

                      {item.public_id && (
                        <p className="text-[11px] text-white/50 mt-[2px]">
                          {item.public_id}
                        </p>
                      )}

                      <p className="text-xs text-white/60">
                        × {item.qty}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default OrdersPage;
